import {
  FIELD_TYPE_CATALOGUE,
  formatFieldValue,
  toneOf,
  type FieldFormat,
  type FieldType,
  type ValueTone,
} from '@crm/contracts';
import { TONE_VARIANT } from '../../lib/value-tones.ts';
import { Badge } from '../ui/badge.tsx';

/**
 * Draw a value by its field TYPE (RELAY-941): the catalogue's `cellRenderer` decides, so a
 * custom field of any type renders like a built-in column of that type. Add a type = add a
 * renderer name to the catalogue and a case here.
 */
const text = (v: unknown): string =>
  typeof v === 'string'
    ? v
    : typeof v === 'number' || typeof v === 'boolean'
      ? String(v)
      : JSON.stringify(v);

export function FieldCell({
  type,
  value,
  format,
  tone,
}: {
  type: FieldType;
  value: unknown;
  /** The definition's display format; only scalar text, number and currency cells apply it. */
  format?: FieldFormat | null | undefined;
  /** Colours a badge cell (a stage's outcome) or makes a datetime a date pill; unset, neutral. */
  tone?: ValueTone | undefined;
}) {
  if (value === null || value === undefined || value === '')
    return <span className="text-muted-foreground">—</span>;
  switch (FIELD_TYPE_CATALOGUE[type].cellRenderer) {
    case 'number': {
      const n = Number(value);
      // Text in a number field (a legacy or hand-written row) reads as itself, never "NaN".
      if (!Number.isFinite(n)) return <span>{typeof value === 'string' ? value.trim() : '—'}</span>;
      const shown = format ? formatFieldValue(n, format) : n.toLocaleString();
      const band = toneOf(n, format);
      return band ? (
        <Badge variant={TONE_VARIANT[band]} className="tabular-nums">
          {shown}
        </Badge>
      ) : (
        <span className="tabular-nums">{shown}</span>
      );
    }
    case 'currency': {
      const c = value as { amountMinor?: number; currency?: string };
      const decimals = format?.decimals;
      // The value names its own currency, so a format only adds text around it and fixes decimals.
      return typeof c.amountMinor === 'number' ? (
        <span className="tabular-nums">
          {formatFieldValue(
            (c.amountMinor / 100).toLocaleString(undefined, {
              style: 'currency',
              currency: c.currency ?? 'USD',
              ...(decimals === undefined
                ? {}
                : { minimumFractionDigits: decimals, maximumFractionDigits: decimals }),
            }),
            format,
          )}
        </span>
      ) : null;
    }
    case 'boolean':
      return value ? <Badge variant="success">yes</Badge> : <Badge variant="secondary">no</Badge>;
    case 'date':
      return <span>{text(value).slice(0, 10)}</span>;
    case 'datetime':
      // A toned datetime is a list's age pill: the day alone.
      return tone ? (
        <Badge variant={TONE_VARIANT[tone]} className="tabular-nums">
          {new Date(text(value)).toLocaleDateString()}
        </Badge>
      ) : (
        <span>{new Date(text(value)).toLocaleString()}</span>
      );
    case 'badge':
      return <Badge variant={tone ? TONE_VARIANT[tone] : 'secondary'}>{text(value)}</Badge>;
    case 'badges':
      return (
        <span className="flex flex-wrap gap-1">
          {(Array.isArray(value) ? value : [value]).map((v) => (
            <Badge key={text(v)} variant="outline">
              {text(v)}
            </Badge>
          ))}
        </span>
      );
    case 'link':
      return (
        <a
          href={text(value)}
          target="_blank"
          rel="noreferrer"
          className="text-brand truncate underline"
        >
          {text(value)}
        </a>
      );
    case 'email':
      return (
        <a href={`mailto:${text(value)}`} className="text-brand truncate underline">
          {text(value)}
        </a>
      );
    case 'json':
      return <code className="text-xs">{JSON.stringify(value)}</code>;
    case 'user':
    case 'reference':
      return <code className="text-xs">{text(value).slice(0, 8)}</code>;
    default:
      return <span className="truncate">{formatFieldValue(text(value), format)}</span>;
  }
}
